// silks/src/utils/orderFilterUtils.js
// Filtering for the admin Orders tab (orders AND payment attempts).
// Dates are compared in the admin's own time zone (the browser's), against
// the order's createdAt (stored by MongoDB as a UTC date).

export const EMPTY_FILTERS = { range: "all", from: "", to: "", status: "All", pay: "All", method: "All", q: "" };
// quick ranges shown on the Orders bar (in this order). "1 Day" = today since midnight,
// "3 Days" = today + the 2 days before, "7 Days" = today + the 6 days before.
export const RANGES = [["custom", "Select Date"], ["all", "All Orders"], ["1d", "1 Day"], ["3d", "3 Days"], ["7d", "7 Days"]];
export const ORDER_STATUSES = ["All", "Pending", "Confirmed", "Shipped", "Delivered", "Cancelled"];
export const PAY_STATUSES = ["All", "Paid", "Failed", "Pending", "Cancelled", "Refunded"];

const sod = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const eod = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
// "2026-10-27" (from <input type=date>) → local midnight, NOT UTC midnight
const parseDay = (s, end) => {
  if (!s) return null;
  const [y, m, d] = String(s).split("-").map(Number);
  if (!y || !m || !d) return null;
  return end ? new Date(y, m - 1, d, 23, 59, 59, 999) : new Date(y, m - 1, d);
};

// Missing / partial filter objects (e.g. right after a hot reload) mean "no filter".
const norm = (f) => ({ ...EMPTY_FILTERS, ...(f && typeof f === "object" ? f : {}) });

export function dateRange(f0, now = new Date()) {
  const f = norm(f0);
  const t = sod(now);
  const back = (n) => { const x = new Date(t); x.setDate(x.getDate() - n); return x; };
  switch (f.range) {
    case "1d":
    case "today":     return { from: t, to: eod(now) };
    case "3d":        return { from: back(2), to: eod(now) };
    case "yesterday": return { from: back(1), to: eod(back(1)) };
    case "7d":        return { from: back(6), to: eod(now) };        // today + previous 6 days
    case "30d":       return { from: back(29), to: eod(now) };
    case "custom":    return { from: parseDay(f.from, false), to: parseDay(f.to, true) };
    default:          return { from: null, to: null };
  }
}
const inRange = (date, { from, to }) => {
  if (!from && !to) return true;
  const d = new Date(date);
  if (isNaN(d)) return false;
  return (!from || d >= from) && (!to || d <= to);
};

// ── payment status / method as stored in the database ─────────────────────
export function orderPayGroup(o) {
  const s = String(o.paymentStatus || "").toLowerCase();
  if (/refund/.test(s)) return "Refunded";
  if (/fail/.test(s)) return "Failed";
  if (/cancel/.test(s)) return "Cancelled";
  if (/^(paid|captured|success)/.test(s)) return "Paid";
  return "Pending";                       // "Pending" (COD not yet paid), "Authorized", "Check payment", empty
}
export const attemptPayGroup = (a) => ({ paid: "Paid", refunded: "Refunded", failed: "Failed", cancelled: "Cancelled" }[a.paymentStatus] || "Pending");

const cap = (s) => String(s || "").replace(/^\w/, (c) => c.toUpperCase());
export const orderMethod = (o) => String(o.paymentMethod || o.payment_method || "").trim() || "Not available";
export function attemptMethod(a) {
  if (a.wallet) return `Wallet · ${cap(a.wallet)}`;
  if (a.method === "upi") return "UPI";
  if (a.method === "netbanking") return a.bank ? `Netbanking · ${a.bank}` : "Netbanking";
  if (a.method) return a.method.toUpperCase();
  return "Not chosen";
}

// ── search ─────────────────────────────────────────────────────────────────
const lc = (s) => String(s == null ? "" : s).toLowerCase();
const digits = (s) => String(s == null ? "" : s).replace(/\D/g, "");
export function matchesQuery({ text = [], phones = [] }, q) {
  const t = lc(q).trim();
  if (!t) return true;
  if (text.some((f) => lc(f).includes(t))) return true;
  const d = digits(t);                                  // "98765 43210", "+91 98765…" → digits
  return d.length >= 4 && phones.some((p) => digits(p).includes(d.slice(-10)));
}
const orderFields = (o) => ({
  text: [o._id, o.email, o.userName, o.address?.fullName, o.address?.name, o.paymentId, o.razorpay_id],
  phones: [o.phone, o.address?.phone],
});
const attemptFields = (a) => ({
  text: [a.id, a.orderId, a.customer?.name, a.customer?.email, a.vpa, ...(a.paymentIds || []), ...(a.gatewayRefs || [])],
  phones: [a.customer?.phone],
});

// ── apply ──────────────────────────────────────────────────────────────────
export function applyOrderFilters(orders, f0, now = new Date()) {
  const f = norm(f0);
  const r = dateRange(f, now);
  return (orders || []).filter((o) =>
    (f.status === "All" || (o.status || "Confirmed") === f.status) &&
    (f.pay === "All" || orderPayGroup(o) === f.pay) &&
    (f.method === "All" || orderMethod(o) === f.method) &&
    inRange(o.createdAt || o.created_at, r) &&
    matchesQuery(orderFields(o), f.q));
}
// Order status (Shipped, Delivered…) does not apply to checkouts without an order.
export function applyAttemptFilters(list, f0, now = new Date()) {
  const f = norm(f0);
  const r = dateRange(f, now);
  return (list || []).filter((a) =>
    (f.pay === "All" || attemptPayGroup(a) === f.pay) &&
    (f.method === "All" || attemptMethod(a) === f.method) &&
    inRange(a.createdAt, r) &&
    matchesQuery(attemptFields(a), f.q));
}
export const isFiltered = (f0, status = "All") => {
  const f = norm(f0);
  return status !== "All" || f.range !== "all" || f.pay !== "All" || f.method !== "All" || !!String(f.q || "").trim();
};